import { Box, MenuItem, Select, Stack, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { DurationValue } from "./fluss-builder-types";

export interface DurationInputProps {
    value: DurationValue;
    onChange: (value: DurationValue) => void;
    allowBusinessHours?: boolean;
    disabled?: boolean;
}

export const DurationInput = ({
    value,
    onChange,
    allowBusinessHours = false,
    disabled = false,
}: DurationInputProps) => {
    const { t } = useTranslation();
    const units: Array<{ value: DurationValue["unit"]; label: string }> = [
        {
            value: "minutes",
            label: String(t("fluss.builder.duration.minutes", { defaultValue: "Minutes" })),
        },
        {
            value: "hours",
            label: String(t("fluss.builder.duration.hours", { defaultValue: "Hours" })),
        },
        ...(allowBusinessHours ? [{
            value: "business_hours" as const,
            label: String(t("fluss.builder.duration.business_hours", { defaultValue: "Business hours" })),
        }] : []),
        {
            value: "days",
            label: String(t("fluss.builder.duration.days", { defaultValue: "Days" })),
        },
    ];

    return (
        // PRIM-033 (round 9): stay inside narrow sidebars; the unit select may shrink.
        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ width: "100%", minWidth: 0 }}>
            <Box sx={{ width: 112, flexShrink: 0 }}>
                <Typography variant="caption" color="text.secondary">{t("fluss.builder.duration.wait", { defaultValue: "Esperar" })}</Typography>
            <TextField
                size="small"
                type="number"
                value={value.amount ?? ""}
                disabled={disabled}
                inputProps={{
                    min: 0,
                    "aria-label": String(t("fluss.builder.duration.amount", { defaultValue: "Duration" })),
                }}
                sx={{
                    width: 112,
                    flexShrink: 0,
                    // PRIM-PILOT-046 (ACH-044): fluss-studio.scss:235 aplica
                    // `.fluss-node-properties .MuiFormControl-root { width: 100% }` (0,2,0), que vencia o
                    // `width: 70` emitido pelo sx (0,1,0) e esticava este controle para a largura toda do
                    // painel; com flexShrink: 0 isso garantia overflow horizontal. O seletor auto-composto
                    // abaixo vale 0,3,0 e ganha independentemente da ordem de injeção das folhas.
                    // PRIM-033 tratou minWidth/flexShrink, que nunca eram o constraint que ligava.
                    "&.MuiTextField-root.MuiFormControl-root": { width: 112 },
                }}
                onChange={(event) => onChange({
                    ...value,
                    amount: event.target.value === "" ? null : Number(event.target.value),
                })}
            />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="caption" color="text.secondary">{t("fluss.builder.duration.unit_label", { defaultValue: "Unidade" })}</Typography>
            <Select
                fullWidth
                size="small"
                value={value.unit}
                disabled={disabled}
                inputProps={{
                    "aria-label": String(t("fluss.builder.duration.unit", { defaultValue: "Duration unit" })),
                }}
                sx={{ minWidth: 0 }}
                onChange={(event) => onChange({
                    ...value,
                    unit: event.target.value as DurationValue["unit"],
                })}
            >
                {units.map((unit) => (
                    <MenuItem key={unit.value} value={unit.value}>{unit.label}</MenuItem>
                ))}
            </Select>
            </Box>
        </Stack>
    );
};

export default DurationInput;
